/**
 * Collection Tabs Image v1.0.0
 * Gestion des onglets pour la section collection-tabs-image
 */

(() => {
  "use strict";

  class CollectionTabsImage {
    constructor(container) {
      this.container = container;
      this.hoverTimeout = null;
      this.hoverDelay = 150; // Délai en ms avant de changer l'image au survol
      this.init();
    }

    init() {
      this.bindEvents();
    }

    bindEvents() {
      // Événements spécifiques à ce conteneur
      this.container.addEventListener("click", (event) => {
        if (event.target.closest(".collection-tab")) {
          this.handleTabClickHover(event);
        }
      });

      this.container.addEventListener("keydown", (event) => {
        if (event.target.closest(".collection-tab")) {
          this.handleKeydown(event);
        }
      });

      // Nouveaux événements pour le survol
      this.container.addEventListener(
        "mouseenter",
        (event) => {
          if (event.target.closest(".collection-tab")) {
            this.handleTabHover(event);
            this.handleTabClickHover(event);
          }
        },
        true
      );

      this.container.addEventListener(
        "mouseleave",
        (event) => {
          if (event.target.closest(".collection-tab")) {
            this.handleTabLeave(event);
          }
        },
        true
      );
    }

    handleTabClickHover(event) {
      const tab = event.target.closest(".collection-tab");
      const container = tab.closest(".collection-tabs-container");

      if (!container) return;

      const tabIndex = tab.dataset.tabIndex;
      const collectionId = tab.dataset.collectionId;

      // Annuler tout timeout de survol en cours
      if (this.hoverTimeout) {
        clearTimeout(this.hoverTimeout);
        this.hoverTimeout = null;
      }

      this.switchTab(container, tabIndex, collectionId, true); // true pour indiquer un clic
    }

    handleTabHover(event) {
      const tab = event.target.closest(".collection-tab");
      const container = tab.closest(".collection-tabs-container");

      if (!container) return; // Supprimer la vérification de l'onglet actif

      const tabIndex = tab.dataset.tabIndex;
      const collectionId = tab.dataset.collectionId;

      // Annuler tout timeout précédent
      if (this.hoverTimeout) {
        clearTimeout(this.hoverTimeout);
      }

      // Programmer le changement d'image avec un délai
      this.hoverTimeout = setTimeout(() => {
        this.switchTabImage(container, tabIndex, collectionId);
      }, this.hoverDelay);
    }

    handleTabLeave(event) {
      // Annuler le timeout si on quitte l'onglet avant le délai
      if (this.hoverTimeout) {
        clearTimeout(this.hoverTimeout);
        this.hoverTimeout = null;
      }
    }

    handleKeydown(event) {
      const tab = event.target.closest(".collection-tab");
      const container = tab.closest(".collection-tabs-container");

      if (!container) return;

      const tabs = container.querySelectorAll(".collection-tab");
      const currentIndex = Array.from(tabs).indexOf(tab);

      switch (event.key) {
        case "ArrowRight":
        case "ArrowDown":
          event.preventDefault();
          const nextIndex = (currentIndex + 1) % tabs.length;
          this.activateTab(tabs[nextIndex], container);
          break;

        case "ArrowLeft":
        case "ArrowUp":
          event.preventDefault();
          const prevIndex =
            currentIndex === 0 ? tabs.length - 1 : currentIndex - 1;
          this.activateTab(tabs[prevIndex], container);
          break;

        case "Home":
          event.preventDefault();
          this.activateTab(tabs[0], container);
          break;

        case "End":
          event.preventDefault();
          this.activateTab(tabs[tabs.length - 1], container);
          break;
      }
    }

    // Nouvelle méthode pour changer uniquement l'image (survol)
    switchTabImage(container, tabIndex, collectionId) {
      const panels = container.querySelectorAll(".collection-tab-panel");
      const contentContainer = container.querySelector(
        ".collection-tabs-content"
      );

      // Masquer tous les panneaux
      panels.forEach((panel) => {
        panel.classList.remove("active");
        panel.style.opacity = "0";
        panel.style.visibility = "hidden";
        panel.style.pointerEvents = "none";
      });

      // Afficher le panneau correspondant
      const activePanel = container.querySelector(
        `.collection-tab-panel[data-tab-index="${tabIndex}"]`
      );

      if (activePanel) {
        // Calculer la hauteur du panneau
        const panelHeight = activePanel.scrollHeight;

        // Appliquer la hauteur au conteneur
        if (contentContainer) {
          contentContainer.style.height = `${panelHeight}px`;
        }

        // Afficher le panneau avec une transition plus rapide pour le survol
        requestAnimationFrame(() => {
          activePanel.classList.add("active");
          activePanel.style.opacity = "1";
          activePanel.style.visibility = "visible";
          activePanel.style.pointerEvents = "auto";
        });
      }
    }

    // Méthode complète pour le clic (change onglet + image)
    switchTab(container, tabIndex, collectionId, isClick = false) {
      const tabs = container.querySelectorAll(".collection-tab");
      const panels = container.querySelectorAll(".collection-tab-panel");
      const contentContainer = container.querySelector(
        ".collection-tabs-content"
      );

      // Si c'est un clic, changer l'état des onglets
      if (isClick) {
        // Désactiver tous les onglets
        tabs.forEach((tab) => {
          tab.classList.remove("active");
          tab.setAttribute("aria-selected", "false");
        });

        // Activer l'onglet sélectionné
        const activeTab = container.querySelector(
          `.collection-tab[data-tab-index="${tabIndex}"]`
        );

        if (activeTab) {
          activeTab.classList.add("active");
          activeTab.setAttribute("aria-selected", "true");
          activeTab.focus();
        }
      }

      // Masquer tous les panneaux
      panels.forEach((panel) => {
        panel.classList.remove("active");
        panel.style.opacity = "0";
        panel.style.visibility = "hidden";
        panel.style.pointerEvents = "none";
      });

      // Afficher le panneau correspondant
      const activePanel = container.querySelector(
        `.collection-tab-panel[data-tab-index="${tabIndex}"]`
      );

      if (activePanel) {
        // Calculer la hauteur du panneau actif
        const panelHeight = activePanel.scrollHeight;

        // Appliquer la hauteur au conteneur pour éviter le saut
        if (contentContainer) {
          contentContainer.style.height = `${panelHeight}px`;
        }

        // Afficher le panneau
        requestAnimationFrame(() => {
          activePanel.classList.add("active");
          activePanel.style.opacity = "1";
          activePanel.style.visibility = "visible";
          activePanel.style.pointerEvents = "auto";
        });
      }
    }

    activateTab(tab, container) {
      const tabIndex = tab.dataset.tabIndex;
      const collectionId = tab.dataset.collectionId;
      this.switchTab(container, tabIndex, collectionId, true);
    }
  }

  // Initialiser une instance par conteneur
  document.addEventListener("DOMContentLoaded", () => {
    const containers = document.querySelectorAll(".collection-tabs-container");
    containers.forEach((container) => {
      new CollectionTabsImage(container);
    });
  });
})();
